import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import { PageEditable } from '@/components/edit/PageEditable';
import { usePageEditMode } from '@/context/PageEditModeContext';
import InlineClassField from '@/components/edit/InlineClassField';
import { toast } from 'sonner';

/**
 * FlyerCircular — 12+ circular slots arranged in a perfect ring around
 * a center schedule card. Sized 1300 × 1820 (30% larger than the previous
 * 1000 × 1400 design). Every child dimension is scaled proportionally.
 */
const API = process.env.REACT_APP_BACKEND_URL || '';

const CANVAS_W = 1300;
const CANVAS_H = 1820;
const RING_CX = 650;
const RING_CY = 949;
const RING_R = 520;
const CIRCLE_SIZE = 189;

const positionFor = (idx, total) => {
  const step = 360 / Math.max(1, total);
  const angle = (-90 + idx * step) * (Math.PI / 180);
  return {
    left: RING_CX + RING_R * Math.cos(angle) - CIRCLE_SIZE / 2,
    top: RING_CY + RING_R * Math.sin(angle) - CIRCLE_SIZE / 2,
  };
};

const LOGO_URL = '/casa-samba-logo.png';

const CircleSlot = ({ media, style, zIndex = 1, editEnabled = false, onUpload, onRename, onDelete }) => {
  const src = media?.url ? `${API}${media.url}` : null;
  const isVideo = src && (src.endsWith('.mp4') || src.endsWith('.webm'));
  const fileRef = useRef(null);
  const handlePick = () => fileRef.current && fileRef.current.click();
  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f && onUpload) onUpload(media.id, f);
    e.target.value = '';
  };
  return (
    <div
      style={{
        position: 'absolute',
        width: CIRCLE_SIZE,
        textAlign: 'center',
        zIndex,
        transform: 'scale(0.8)',
        ...style,
      }}
    >
      <div
        onClick={editEnabled ? handlePick : undefined}
        title={editEnabled ? 'Click to replace image' : ''}
        style={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          overflow: 'hidden',
          border: editEnabled ? '4px dashed #f4b400' : '4px solid #f4b400',
          boxShadow: '0 10px 32px rgba(0,0,0,0.5)',
          background: '#111',
          cursor: editEnabled ? 'pointer' : 'default',
          position: 'relative',
        }}
      >
        {src ? (
          isVideo ? (
            <video
              src={src}
              autoPlay
              loop
              muted
              playsInline
              crossOrigin="anonymous"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <img
              src={src}
              alt={media?.label}
              crossOrigin="anonymous"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(244,180,0,0.35)',
              fontSize: 13,
              letterSpacing: 2,
              textTransform: 'uppercase',
              fontWeight: 900,
              padding: 10,
              textAlign: 'center',
            }}
          >
            {media?.label || ''}
          </div>
        )}
        {editEnabled && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.35)',
              color: '#f4b400',
              fontWeight: 900,
              fontSize: 14,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              pointerEvents: 'none',
            }}
          >
            📷 Change
          </div>
        )}
        {editEnabled && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(media.id, media.label); }}
            title="Delete this circle"
            style={{
              position: 'absolute', top: 6, right: 6,
              width: 28, height: 28, borderRadius: '50%',
              background: '#b91c1c', color: '#fff', border: '2px solid #fff',
              fontWeight: 900, fontSize: 14, cursor: 'pointer', lineHeight: 1,
              zIndex: 2,
            }}
          >×</button>
        )}
      </div>
      {editEnabled && (
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/mp4,video/webm"
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      )}
      <div
        style={{
          marginTop: 10,
          fontSize: 16,
          fontWeight: 900,
          color: '#fff',
          textTransform: 'uppercase',
          letterSpacing: 1,
          lineHeight: 1.15,
          textShadow: '0 2px 8px rgba(0,0,0,0.9)',
        }}
      >
        {editEnabled ? (
          <span
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => {
              const next = e.currentTarget.textContent.trim();
              if (next && next.toUpperCase() !== (media?.label || '').toUpperCase() && onRename) {
                onRename(media.id, next);
              }
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
            }}
            style={{
              outline: '2px dashed rgba(244,180,0,0.7)',
              outlineOffset: 2,
              borderRadius: 3,
              cursor: 'text',
            }}
          >
            {(media?.label || '').toUpperCase()}
          </span>
        ) : (
          (media?.label || '').toUpperCase()
        )}
      </div>
    </div>
  );
};

const FlyerCircular = React.forwardRef(function FlyerCircular({ classes = [], onClassesChanged }, ref) {
  const { enabled: editEnabled } = usePageEditMode();
  const [orderedSlots, setOrderedSlots] = useState([]);
  const [qrInPerson, setQrInPerson] = useState(null);
  const [qrPrivate, setQrPrivate] = useState(null);
  const [qrUrls, setQrUrls] = useState({ in_person_url: '', private_url: '' });

  const loadSlots = async () => {
    try {
      const res = await fetch(`${API}/api/flyer-gifs/slots`);
      const data = await res.json();
      setOrderedSlots(data.slots || []);
    } catch {
      setOrderedSlots([]);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/api/flyer-gifs/settings`);
      const s = await res.json();
      setQrUrls({ in_person_url: s.in_person_url || '', private_url: s.private_url || '' });
      const opts = { margin: 1, width: 400, color: { dark: '#000000', light: '#ffffff' } };
      setQrInPerson(s.in_person_url ? await QRCode.toDataURL(s.in_person_url, opts) : null);
      setQrPrivate(s.private_url ? await QRCode.toDataURL(s.private_url, opts) : null);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadSlots(); }, []);
  useEffect(() => { loadSettings(); }, []);

  const handleUpload = async (slotId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/flyer-gifs/upload/${slotId}`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Upload failed');
      toast.success('Image updated');
      await loadSlots();
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    }
  };

  const handleRename = async (slotId, label) => {
    try {
      const res = await fetch(`${API}/api/flyer-gifs/slots/${slotId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      if (!res.ok) throw new Error('Rename failed');
      toast.success('Label saved');
      await loadSlots();
    } catch (e) {
      toast.error(e.message || 'Rename failed');
    }
  };

  const saveQrUrl = async (patch) => {
    const next = { ...qrUrls, ...patch };
    try {
      const res = await fetch(`${API}/api/flyer-gifs/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('Save failed');
      setQrUrls(next);
      toast.success('QR link saved');
      await loadSettings();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  const addSlot = async () => {
    const label = window.prompt('New circle label (e.g. "Kids Samba"):');
    if (!label || !label.trim()) return;
    try {
      const res = await fetch(`${API}/api/flyer-gifs/slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: label.trim() }),
      });
      if (!res.ok) throw new Error('Add failed');
      toast.success('Circle added — click it to upload an image');
      await loadSlots();
    } catch (e) {
      toast.error(e.message || 'Add failed');
    }
  };

  const deleteSlot = async (slotId, label) => {
    if (!window.confirm(`Delete "${label}"?`)) return;
    try {
      const res = await fetch(`${API}/api/flyer-gifs/slots/${slotId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Circle removed');
      await loadSlots();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  return (
    <div
      ref={ref}
      data-testid="flyer-circular"
      style={{
        position: 'relative',
        width: CANVAS_W,
        height: CANVAS_H,
        background:
          'radial-gradient(ellipse at center, #1a0f0a 0%, #0a0a0a 65%), #0a0a0a',
        color: '#fff',
        fontFamily: 'Georgia, serif',
        overflow: 'hidden',
      }}
    >
      {/* Diagonal texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(135deg, transparent 0, transparent 31px, rgba(244,180,0,0.03) 31px, rgba(244,180,0,0.03) 34px)',
        }}
      />

      {/* Faint gold guide ring behind the circles */}
      <div
        style={{
          position: 'absolute',
          left: RING_CX - RING_R,
          top: RING_CY - RING_R,
          width: RING_R * 2,
          height: RING_R * 2,
          borderRadius: '50%',
          border: '1px dashed rgba(244,180,0,0.15)',
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 57,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 5,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            background: '#f4b400',
            color: '#000',
            padding: '7px 21px',
            borderRadius: 999,
            fontSize: 16,
            fontWeight: 900,
            letterSpacing: 4,
            textTransform: 'uppercase',
            marginBottom: 13,
          }}
        >
          <PageEditable path="flyer.pill" as="span" fallback="Escola de Samba · Since 1987" />
        </div>
        <div
          style={{
            fontSize: 75,
            fontWeight: 900,
            letterSpacing: -1.5,
            lineHeight: 1,
          }}
        >
          <PageEditable path="flyer.title" as="span" fallback="THE CASA SAMBA CLASSES" />
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 26,
            fontWeight: 700,
            color: '#f4b400',
            letterSpacing: 1.3,
          }}
        >
          <PageEditable path="flyer.subtitle" as="span" fallback="Afro-Brazilian Culture · Dance · Drum" />
        </div>
      </div>

      {/* Registration QR codes — flanking the top corners, above the ring */}
      {qrInPerson && (
        <div
          style={{
            position: 'absolute',
            left: 40,
            top: 250,
            width: 180,
            textAlign: 'center',
            zIndex: 6,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: 8,
              borderRadius: 14,
              border: '4px solid #f4b400',
              boxShadow: '0 10px 32px rgba(0,0,0,0.5)',
            }}
          >
            <img
              src={qrInPerson}
              alt="In-Person Registration"
              crossOrigin="anonymous"
              style={{ width: 160, height: 160, display: 'block' }}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: 15,
              fontWeight: 900,
              color: '#f4b400',
              textTransform: 'uppercase',
              letterSpacing: 2,
              textShadow: '0 2px 8px rgba(0,0,0,0.9)',
            }}
          >
            Scan · In-Person
          </div>
          {editEnabled && (
            <input
              type="url"
              defaultValue={qrUrls.in_person_url}
              placeholder="https://... QR link for In-Person"
              onBlur={(e) => saveQrUrl({ in_person_url: e.target.value.trim() })}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={{
                marginTop: 6, width: '100%', fontSize: 11, padding: '6px 8px',
                borderRadius: 6, border: '2px dashed #f4b400', background: '#111', color: '#f4b400',
              }}
            />
          )}
        </div>
      )}
      {editEnabled && !qrInPerson && (
        <div style={{ position: 'absolute', left: 40, top: 250, width: 180, textAlign: 'center', zIndex: 6 }}>
          <div style={{
            width: 176, height: 176, borderRadius: 14, border: '4px dashed #f4b400',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#f4b400', fontWeight: 900, fontSize: 12, letterSpacing: 1.5, textAlign: 'center', padding: 10,
          }}>
            Paste In-Person link below
          </div>
          <input
            type="url"
            defaultValue={qrUrls.in_person_url}
            placeholder="https://... QR link for In-Person"
            onBlur={(e) => saveQrUrl({ in_person_url: e.target.value.trim() })}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') e.currentTarget.blur(); }}
            style={{
              marginTop: 6, width: '100%', fontSize: 11, padding: '6px 8px',
              borderRadius: 6, border: '2px dashed #f4b400', background: '#111', color: '#f4b400',
            }}
          />
        </div>
      )}
      {qrPrivate && (
        <div
          style={{
            position: 'absolute',
            right: 40,
            top: 250,
            width: 180,
            textAlign: 'center',
            zIndex: 6,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: 8,
              borderRadius: 14,
              border: '4px solid #f4b400',
              boxShadow: '0 10px 32px rgba(0,0,0,0.5)',
            }}
          >
            <img
              src={qrPrivate}
              alt="Private Class Registration"
              crossOrigin="anonymous"
              style={{ width: 160, height: 160, display: 'block' }}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: 15,
              fontWeight: 900,
              color: '#f4b400',
              textTransform: 'uppercase',
              letterSpacing: 2,
              textShadow: '0 2px 8px rgba(0,0,0,0.9)',
            }}
          >
            Scan · Private
          </div>
          {editEnabled && (
            <input
              type="url"
              defaultValue={qrUrls.private_url}
              placeholder="https://... QR link for Private"
              onBlur={(e) => saveQrUrl({ private_url: e.target.value.trim() })}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={{
                marginTop: 6, width: '100%', fontSize: 11, padding: '6px 8px',
                borderRadius: 6, border: '2px dashed #f4b400', background: '#111', color: '#f4b400',
              }}
            />
          )}
        </div>
      )}
      {editEnabled && !qrPrivate && (
        <div style={{ position: 'absolute', right: 40, top: 250, width: 180, textAlign: 'center', zIndex: 6 }}>
          <div style={{
            width: 176, height: 176, borderRadius: 14, border: '4px dashed #f4b400',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#f4b400', fontWeight: 900, fontSize: 12, letterSpacing: 1.5, textAlign: 'center', padding: 10,
          }}>
            Paste Private link below
          </div>
          <input
            type="url"
            defaultValue={qrUrls.private_url}
            placeholder="https://... QR link for Private"
            onBlur={(e) => saveQrUrl({ private_url: e.target.value.trim() })}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') e.currentTarget.blur(); }}
            style={{
              marginTop: 6, width: '100%', fontSize: 11, padding: '6px 8px',
              borderRadius: 6, border: '2px dashed #f4b400', background: '#111', color: '#f4b400',
            }}
          />
        </div>
      )}

      {/* Circles — evenly spaced around the ring. zIndex inverse to order so
          earlier positions render on top (label of upper circle stays visible
          over the circle below it — fixes "Learn the Samba" hiding behind Cabolcu). */}
      {orderedSlots.map((slot, i) => (
        <CircleSlot
          key={slot.id}
          media={slot}
          style={positionFor(i, orderedSlots.length)}
          zIndex={i + 1}
          editEnabled={editEnabled}
          onUpload={handleUpload}
          onRename={handleRename}
          onDelete={deleteSlot}
        />
      ))}

      {editEnabled && (
        <>
          <div
            style={{
              position: 'absolute', top: 12, left: 12, right: 12,
              padding: '10px 16px', zIndex: 20,
              background: 'rgba(244,180,0,0.15)', border: '2px dashed #f4b400',
              borderRadius: 10, color: '#f4b400', fontSize: 14, fontWeight: 700,
              textAlign: 'center', letterSpacing: 0.5,
            }}
          >
            EDIT MODE — click any circle to swap the image, edit the label under it, edit any yellow-outlined text, or paste new QR URLs. Click Save (bottom-left) when done.
          </div>
          <button
            onClick={addSlot}
            style={{
              position: 'absolute', left: RING_CX - 90, top: RING_CY + RING_R + 40,
              padding: '10px 22px', borderRadius: 999, border: '2px solid #f4b400',
              background: '#f4b400', color: '#000', fontWeight: 900, letterSpacing: 1.2,
              cursor: 'pointer', zIndex: 20, fontSize: 14, textTransform: 'uppercase',
            }}
          >+ Add Circle</button>
        </>
      )}

      {/* Center schedule card */}
      <div
        style={{
          position: 'absolute',
          left: 370,
          top: 655,
          width: 560,
          height: 620,
          background: 'linear-gradient(180deg, rgba(28,20,10,0.95) 0%, rgba(10,10,10,0.98) 100%)',
          border: '3px solid #f4b400',
          borderRadius: 26,
          padding: '26px 30px',
          boxShadow: '0 26px 78px rgba(0,0,0,0.7)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 4,
        }}
      >
        <div
          style={{
            fontSize: 16,
            letterSpacing: 4,
            fontWeight: 900,
            color: '#f4b400',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: 11,
          }}
        >
          <PageEditable path="flyer.schedule_heading" as="span" fallback="Regular Class Schedule" />
        </div>
        <div style={{ display: 'grid', gap: 8, flex: 1, overflow: 'hidden' }}>
          {[...classes]
            .sort((a, b) => {
              const dayOrder = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
              const da = dayOrder.indexOf(a.day_of_week || '');
              const db = dayOrder.indexOf(b.day_of_week || '');
              if (da !== db) return da - db;
              return (a.start_time || '').localeCompare(b.start_time || '');
            })
            .map((c) => (
            <div
              key={c.id}
              style={{
                padding: '9px 14px',
                background: 'rgba(244,180,0,0.08)',
                border: '1px solid rgba(244,180,0,0.35)',
                borderLeft: '5px solid #f4b400',
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', lineHeight: 1.15 }}>
                <InlineClassField
                  classId={c.id}
                  field="name"
                  value={c.name}
                  onSaved={onClassesChanged}
                  inputClassName="w-full"
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.2, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <InlineClassField
                    classId={c.id}
                    field="level"
                    value={c.level || ''}
                    onSaved={onClassesChanged}
                    options={[
                      { value: 'all', label: 'All' },
                      { value: 'beginner', label: 'Beginner' },
                      { value: 'intermediate', label: 'Intermediate' },
                      { value: 'advanced', label: 'Advanced' },
                    ]}
                  />
                  {!editEnabled && <span>· {c.category === 'kids' ? 'Kids' : 'Adults'}</span>}
                  {editEnabled && (
                    <>
                      ·
                      <InlineClassField
                        classId={c.id}
                        field="category"
                        value={c.category || 'adults'}
                        onSaved={onClassesChanged}
                        options={[
                          { value: 'adults', label: 'Adults' },
                          { value: 'kids', label: 'Kids' },
                        ]}
                      />
                    </>
                  )}
                </span>
                <span style={{ fontSize: 13, color: '#f4b400', fontWeight: 800, display: 'flex', gap: 4, alignItems: 'center' }}>
                  <InlineClassField
                    classId={c.id}
                    field="day_of_week"
                    value={c.day_of_week || ''}
                    onSaved={onClassesChanged}
                    options={[
                      { value: 'Monday', label: 'Monday' },
                      { value: 'Tuesday', label: 'Tuesday' },
                      { value: 'Wednesday', label: 'Wednesday' },
                      { value: 'Thursday', label: 'Thursday' },
                      { value: 'Friday', label: 'Friday' },
                      { value: 'Saturday', label: 'Saturday' },
                      { value: 'Sunday', label: 'Sunday' },
                    ]}
                  />
                  ·
                  <InlineClassField
                    classId={c.id}
                    field="start_time"
                    value={c.start_time || ''}
                    onSaved={onClassesChanged}
                    placeholder="7:00 PM"
                  />
                </span>
              </div>
            </div>
          ))}
          {classes.length === 0 && (
            <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '26px 0' }}>
              Contact us for the current weekly schedule.
            </div>
          )}
        </div>

        {/* Price + private classes */}
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <div style={{
            flex: 1,
            background: '#f4b400',
            color: '#000',
            borderRadius: 10,
            padding: '7px 10px',
            textAlign: 'center',
            fontWeight: 900,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 2 }}>
              <PageEditable path="flyer.price_label" as="span" fallback="PER CLASS" />
            </div>
            <div style={{ fontSize: 22, lineHeight: 1 }}>
              <PageEditable path="flyer.price_value" as="span" fallback="$10" />
            </div>
          </div>
          <div style={{
            flex: 1,
            background: 'linear-gradient(90deg, #b91c1c, #f4b400)',
            color: '#fff',
            borderRadius: 10,
            padding: '7px 10px',
            textAlign: 'center',
            fontWeight: 900,
          }}>
            <div style={{ fontSize: 11, letterSpacing: 2 }}>
              <PageEditable path="flyer.first_label" as="span" fallback="FIRST CLASS" />
            </div>
            <div style={{ fontSize: 20, lineHeight: 1 }}>
              <PageEditable path="flyer.first_value" as="span" fallback="FREE!" />
            </div>
          </div>
        </div>
        <div style={{
          marginTop: 8,
          textAlign: 'center',
          fontSize: 12,
          fontWeight: 900,
          color: '#f4b400',
          letterSpacing: 1.2,
          textTransform: 'uppercase',
        }}>
          <PageEditable path="flyer.private_note" as="span" fallback="🥁 Private Classes Available · By Appointment" />
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          position: 'absolute',
          left: 52,
          right: 52,
          bottom: 28,
          display: 'flex',
          alignItems: 'center',
          gap: 21,
          borderTop: '3px solid rgba(244,180,0,0.35)',
          paddingTop: 18,
          zIndex: 5,
        }}
      >
        <img
          src={LOGO_URL}
          alt="Casa Samba"
          crossOrigin="anonymous"
          style={{ width: 86, height: 86, objectFit: 'contain' }}
        />
        <div style={{ flex: 1, fontSize: 17, lineHeight: 1.4 }}>
          <div style={{ fontWeight: 900, fontSize: 23, color: '#f4b400' }}>
            <PageEditable path="flyer.footer_name" as="span" fallback="Mestre Curtis Pierre" />
          </div>
          <div style={{ opacity: 0.85 }}>
            <PageEditable path="flyer.footer_org" as="span" fallback="Escola de Samba Casa Samba" />
          </div>
          <div style={{ opacity: 0.7, fontSize: 15 }}>
            <PageEditable path="flyer.footer_address" as="span" fallback="N.O.R.D. Annunciation Center · 800 Race St, New Orleans, LA" />
          </div>
        </div>
        <div style={{ textAlign: 'right', color: '#f4b400', fontWeight: 900 }}>
          <div style={{ fontSize: 23 }}>
            <PageEditable path="flyer.footer_phone" as="span" fallback="504-236-7479" />
          </div>
          <div style={{ fontSize: 17, opacity: 0.85 }}>
            <PageEditable path="flyer.footer_website" as="span" fallback="casasamba.com" />
          </div>
        </div>
      </div>
    </div>
  );
});

export default FlyerCircular;
